import axiosInstance from './axios';

export interface AdminLoginData {
  email: string;
  password: string;
}

export interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
}

export interface Product {
  id?: string;  // Used internally in frontend
  product_id?: number;  // Returned from backend
  name: string;
  name_zh?: string | null;
  description: string;
  description_zh?: string | null;
  category_id: string;
  base_price: number;
  preparation_time_hours: number;
  active: boolean;
  qr_enabled?: boolean;
  qr_token?: string | null;
  // Whether this product supports BYO cake customization (option-category
  // selections) on the customer-facing product detail page. Like qr_enabled,
  // ProductUpdate on the backend requires the full body on every PUT (no true
  // partial-PATCH), so create()/update() below must always send this field,
  // not omit it when unchanged.
  customizable?: boolean;
  loyalty_points_rate?: number | null;
  sizes?: ProductSize[];
  images?: ProductImage[];
  allergies?: string[];
  // Echoed back from GET on the product the admin edit form loaded, and
  // sent back on update() so the backend can reject a stale-read save
  // (409) instead of silently losing a concurrently-added size. Optional
  // so create()/new-product flows are unaffected.
  updated_at?: string | null;
}

export interface ProductSize {
  id?: string;
  size_name: string;
  size_name_zh?: string | null;
  price_modifier: number;
  active: boolean;
}

export interface ProductImage {
  id?: string;
  image_url: string;
  is_primary: boolean;
  sort_order: number;
}

export interface Category {
  id?: string;
  name: string;
  name_zh?: string;
  description: string;
  description_zh?: string;
  image_url?: string;
  parent_category_id?: string | null;
  sort_order: number;
  active: boolean;
  // Echoed back from GET on the category the admin edit form loaded, and
  // sent back on update() so the backend can reject a stale-read save
  // (409) instead of silently losing a concurrently-saved change. Optional
  // so create()/new-category flows are unaffected.
  updated_at?: string | null;
}

export interface Order {
  order_id: number;  // Changed to order_id to match backend response
  order_number: string;
  status: string;
  total_amount: number;
  pickup_date: string;
  pickup_time: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  special_instructions?: string;
  payment_status?: string;
  created_at: string;
  updated_at: string;
  items?: OrderItem[];
}

// A single BYO/customizable-product option selection, snapshotted onto the
// order item at checkout time. Mirrors
// backend/app/schemas/order.py's OrderItemResponse.selected_options entries.
export interface SelectedOption {
  category_id: number;
  category_name: string | null;
  item_id: number;
  item_name: string;
  price_modifier: number;
}

export interface OrderItem {
  order_item_id: number;  // Matches backend OrderItemResponse.order_item_id
  product_id: number;  // Changed to number
  product_name: string;
  // Null when the item has no size (e.g. not a sized product); populated
  // from backend/app/db/models.py's OrderItem.size_name property (task-045).
  size_name?: string | null;
  quantity: number;
  unit_price: number;
  subtotal: number;
  // Populated for customizable products; null/undefined when the item has
  // no customization selections (regular non-customizable products, or
  // orders placed before this feature existed).
  selected_options?: SelectedOption[] | null;
  // Customer-uploaded cake photo URL (customizable products); null/absent otherwise.
  custom_image_url?: string | null;
}

export interface DashboardData {
  total_orders: number;
  pending_orders: number;
  total_revenue: number;
  today_orders: number;
  top_products: Array<{
    id: string;
    name: string;
    total_sold: number;
  }>;
}

// GET /admin/dashboard/quick-bites response shape (Drink & Bite tab). Mirrors
// get_quick_bites_dashboard_data in backend/app/api/admin.py. Note `id` is the
// integer BubbleTeaMenuItem.menu_item_id (unlike DashboardData's string id).
export interface QBDashboardData {
  total_orders: number;
  pending_orders: number;
  active_orders: number;
  today_orders: number;
  total_revenue: number;
  top_products: Array<{
    id: number;
    name: string;
    total_sold: number;
  }>;
}

export const adminAuth = {
  login: async (data: AdminLoginData) => {
    // Backend expects OAuth2 form data format
    const formData = new URLSearchParams();
    formData.append('username', data.email); // OAuth2 spec uses 'username' field
    formData.append('password', data.password);

    const response = await axiosInstance.post<{ access_token: string; token_type: string }>('/auth/admin/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    // The backend doesn't return user data directly, we'd need to fetch it separately
    // For now, just return the token info and decode the user from JWT if needed
    return {
      access_token: response.data.access_token,
      user: {
        id: '',
        email: data.email,
        first_name: 'Admin',
        last_name: 'User',
        role: 'admin',
      } as AdminUser
    };
  },

  logout: () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  },
};

export const adminProducts = {
  getAll: async () => {
    // Use the admin products endpoint to get full details including images.
    // include_inactive=true so deactivated products remain visible (with an
    // Inactive badge) and reachable for reactivation via the admin grid.
    // limit=1000 is a stopgap for J55: the backend defaults to limit=100
    // (now ordered newest-first via product_id desc, see admin.py), and this
    // dev DB already has 200+ rows (mostly disposable QA fixtures -- there's
    // no hard-delete path). Raising the cap here avoids truncating the grid
    // for now, but it is still a fixed ceiling, not real pagination: if the
    // true row count ever grows past 1000, this same truncation bug can
    // resurface and will need an actual paginated/infinite-scroll UI.
    const response = await axiosInstance.get<any[]>('/admin/products?include_inactive=true&limit=1000');
    // Get base URL without /api suffix for local image URLs
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '');
    // Map product_id to id for frontend consistency
    return response.data
      .map(product => ({
        ...product,
        id: product.product_id?.toString(),
        category_id: product.category_id?.toString() || '',
        images: product.images?.map((img: any) => ({
          ...img,
          // S3 URLs are already full URLs (https://...), local URLs need baseUrl prepended
          image_url: img.image_url.startsWith('http') ? img.image_url : `${baseUrl}${img.image_url}`
        })) || [],
        sizes: product.sizes || [],
        allergies: product.allergies?.map((allergy: any) =>
          typeof allergy === 'string' ? allergy : allergy.allergy_type
        ) || []
      }));
  },

  getById: async (id: string) => {
    const response = await axiosInstance.get<Product>(`/admin/products/${id}`);
    return response.data;
  },

  create: async (product: Product) => {
    // Transform the product data to match backend schema
    const productData = {
      name: product.name,
      name_zh: product.name_zh ?? null,
      description: product.description,
      description_zh: product.description_zh ?? null,
      category_id: product.category_id ? parseInt(product.category_id) : null,
      base_price: product.base_price,
      preparation_time_hours: product.preparation_time_hours,
      active: product.active ?? true,
      qr_enabled: product.qr_enabled ?? false,
      customizable: product.customizable ?? false,
      loyalty_points_rate: product.loyalty_points_rate ?? null,
      image_urls: product.images?.map(img => img.image_url) || [],
      allergies: product.allergies || []
    };

    const response = await axiosInstance.post<any>('/admin/products', productData);
    return {
      ...response.data,
      id: response.data.product_id?.toString(),
      category_id: response.data.category_id?.toString() || ''
    };
  },

  update: async (id: string, product: Product) => {
    // Transform the product data to match backend schema
    const productData = {
      name: product.name,
      name_zh: product.name_zh ?? null,
      description: product.description,
      description_zh: product.description_zh ?? null,
      category_id: product.category_id ? parseInt(product.category_id) : null,
      base_price: product.base_price,
      preparation_time_hours: product.preparation_time_hours,
      active: product.active,
      qr_enabled: product.qr_enabled ?? false,
      customizable: product.customizable ?? false,
      loyalty_points_rate: product.loyalty_points_rate ?? null,
      image_urls: product.images?.map(img => img.image_url) || [],
      allergies: product.allergies || [],
      // Round-trips the updated_at this product was loaded with, so the
      // backend can 409 if someone else saved a change in the meantime
      // (see backend/app/api/admin.py's update_product).
      updated_at: product.updated_at ?? null
    };

    console.log('Updating product with data:', productData);
    const response = await axiosInstance.put<any>(`/admin/products/${id}`, productData);
    return {
      ...response.data,
      id: response.data.product_id?.toString(),
      category_id: response.data.category_id?.toString() || ''
    };
  },

  delete: async (id: string) => {
    await axiosInstance.delete(`/admin/products/${id}`);
  },

  uploadImage: async (productId: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await axiosInstance.post(`/admin/products/${productId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadBase64Image: async (base64Data: string) => {
    const response = await axiosInstance.post('/admin/upload-base64', { image: base64Data });
    return response.data;
  },

  // Rotates the product's qr_token to a new value, invalidating any
  // previously printed/saved QR code. 400s if qr_enabled is false.
  refreshQrCode: async (id: string) => {
    const response = await axiosInstance.post<any>(`/admin/products/${id}/refresh-qr-code`);
    return {
      ...response.data,
      id: response.data.product_id?.toString(),
      category_id: response.data.category_id?.toString() || ''
    };
  },

  // GET /admin/products/{id}/qr-image is admin-gated (Bearer token header),
  // so it can't be used as a plain <img src> -- fetch it through the
  // authenticated axios client as a blob and let the caller build an object
  // URL from it.
  getQrImage: async (id: string) => {
    const response = await axiosInstance.get(`/admin/products/${id}/qr-image`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // GET /admin/products/export returns a PDF of all active products with QR
  // codes. Generation can take a while, so override the default 30s timeout.
  exportQrPdf: async () => {
    const response = await axiosInstance.get('/admin/products/export', {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data as Blob;
  },
};

// Standalone alias matching the task spec's exact function name, for the
// upcoming admin UI QR-refresh action to import directly.
export const refreshProductQrCode = adminProducts.refreshQrCode;

export const adminCategories = {
  getAll: async () => {
    // Use the regular categories endpoint for listing
    const response = await axiosInstance.get<any[]>('/categories');
    // Filter out inactive categories and map category_id to id for frontend consistency
    return response.data
      .filter(cat => cat.active !== false)
      .map(cat => ({
        ...cat,
        id: cat.category_id?.toString() || ''
      }));
  },

  create: async (category: Category) => {
    const response = await axiosInstance.post<Category>('/admin/categories', category);
    return response.data;
  },

  update: async (id: string, category: Category) => {
    const response = await axiosInstance.put<Category>(`/admin/categories/${id}`, category);
    return response.data;
  },

  delete: async (id: string) => {
    await axiosInstance.delete(`/admin/categories/${id}`);
  },
};

// POST /admin/orders/bulk-status response shape. Mirrors
// backend/app/schemas/order.py's BulkOrderStatusResult / BulkOrderStatusResponse.
export interface BulkOrderStatusResult {
  order_id: number;
  order_number: string | null;
  outcome: 'updated' | 'skipped' | 'failed';
  reason: string | null;
}

export interface BulkOrderStatusResponse {
  requested: number;
  updated: number;
  skipped: number;
  failed: number;
  results: BulkOrderStatusResult[];
}

export const adminOrders = {
  getAll: async (filters?: { status?: string; date_from?: string; date_to?: string }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }
    const response = await axiosInstance.get<Order[]>(`/admin/orders?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number) => {
    // Use the regular orders endpoint for getting order details
    const response = await axiosInstance.get<Order>(`/orders/${id}`);
    return response.data;
  },

  updateStatus: async (id: number, status: string) => {
    const response = await axiosInstance.put<Order>(`/admin/orders/${id}/status`, { status });
    return response.data;
  },

  // Explicit IDs only: there is intentionally no filter/"all" variant.
  bulkUpdateStatus: async (orderIds: number[], status: 'completed' | 'cancelled') => {
    const response = await axiosInstance.post<BulkOrderStatusResponse>('/admin/orders/bulk-status', {
      order_ids: orderIds,
      status,
    });
    return response.data;
  },
};

export const adminDashboard = {
  getData: async () => {
    const response = await axiosInstance.get<DashboardData>('/admin/dashboard');
    return response.data;
  },
  getQuickBitesData: async () => {
    const response = await axiosInstance.get<QBDashboardData>('/admin/dashboard/quick-bites');
    return response.data;
  },
};

// GET/PUT /admin/settings/loyalty response shape. Mirrors
// backend/app/schemas/loyalty.py's LoyaltySettingsResponse.
export interface LoyaltySettings {
  points_per_pound: number;
  points_to_currency_rate: number;
  expiry_days: number;
}

// POST /admin/settings/loyalty/apply-to-all-products response. Mirrors
// backend/app/schemas/loyalty.py's ApplyLoyaltyRateToAllProductsResponse.
export interface ApplyLoyaltyRateToAllProductsResponse {
  updated_count: number;
}

export const adminLoyalty = {
  getSettings: async () => {
    const response = await axiosInstance.get<LoyaltySettings>('/admin/settings/loyalty');
    return response.data;
  },

  updateSettings: async (settings: LoyaltySettings) => {
    const response = await axiosInstance.put<LoyaltySettings>('/admin/settings/loyalty', settings);
    return response.data;
  },

  // Bulk-writes the current global points_per_pound rate onto every
  // product's loyalty_points_rate override, regardless of active/inactive
  // status. No request body -- the backend reads the rate to apply from
  // the already-saved settings row.
  applyRateToAllProducts: async () => {
    const response = await axiosInstance.post<ApplyLoyaltyRateToAllProductsResponse>('/admin/settings/loyalty/apply-to-all-products');
    return response.data;
  },
};

// One row of GET /admin/customers -- profile summary + current points
// balance + a cheap combined CC+QB order count. Mirrors
// backend/app/schemas/customer.py's AdminCustomerListItem.
export interface AdminCustomerListItem {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  loyalty_points_balance: number;
  lifetime_discount_percent: number;
  order_count: number;
  created_at: string;
}

export interface AdminCustomerListResponse {
  customers: AdminCustomerListItem[];
  total: number;
}

// A Custom Cakes (Order) row in the combined order history returned by
// GET /admin/customers/{user_id}. Mirrors backend/app/schemas/order.py's
// OrderResponse plus the order_type discriminator added by
// AdminCCOrderHistoryItem -- note order_id/order_number/total_amount, NOT
// id/total (that's the QB variant below).
export interface AdminCCOrderHistoryItem {
  order_type: 'custom_cakes';
  order_id: number;
  order_number: string;
  status: string;
  total_amount: number;
  created_at: string;
  points_earned: number;
  points_redeemed: number;
  points_redeemed_value: number;
  lifetime_discount_percent?: number;
  lifetime_discount_amount?: number;
}

// A Quick Bites (BubbleTeaOrder) row in the combined order history. Mirrors
// backend/app/schemas/bubbletea.py's QBOrderResponse plus the order_type
// discriminator added by AdminQBOrderHistoryItem -- note id/total/
// queue_number, NOT order_id/order_number/total_amount (that's the CC
// variant above).
export interface AdminQBOrderHistoryItem {
  order_type: 'quick_bites';
  id: number;
  queue_number: number | null;
  status: string;
  total: number;
  created_at: string;
  points_earned: number;
  points_redeemed: number;
  points_redeemed_value: number;
  lifetime_discount_percent?: number;
  lifetime_discount_amount?: number;
}

export type AdminOrderHistoryEntry = AdminCCOrderHistoryItem | AdminQBOrderHistoryItem;

// GET /admin/customers/{user_id} -- profile + current points balance + a
// combined order history spanning both CC and QB orders, already sorted
// desc by created_at by the backend. Mirrors
// backend/app/schemas/customer.py's AdminCustomerDetailResponse.
export interface AdminCustomerDetail {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  loyalty_points_balance: number;
  lifetime_discount_percent: number;
  created_at: string;
  order_history: AdminOrderHistoryEntry[];
}

export interface AdminLifetimeDiscountResponse {
  user_id: number;
  lifetime_discount_percent: number;
}

export const adminCustomers = {
  getAll: async (skip: number = 0, limit: number = 20) => {
    const response = await axiosInstance.get<AdminCustomerListResponse>(`/admin/customers?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getById: async (userId: number) => {
    const response = await axiosInstance.get<AdminCustomerDetail>(`/admin/customers/${userId}`);
    return response.data;
  },

  // PUT /admin/customers/{user_id}/lifetime-discount -- admin-only; 0..100, max 2dp.
  updateLifetimeDiscount: async (userId: number, lifetimeDiscountPercent: number) => {
    const response = await axiosInstance.put<AdminLifetimeDiscountResponse>(
      `/admin/customers/${userId}/lifetime-discount`,
      { lifetime_discount_percent: lifetimeDiscountPercent }
    );
    return response.data;
  },
};